// City-specific market data for realistic business planning
import { getCurrencyForCity } from "./currency";

export interface CityLinks {
  businessRegistration: string;
  healthDepartment: string;
  zoning: string;
  alcoholBeerWine: string;
  alcoholFullLiquor: string;
  salesTax: string;
  fireDepartment: string;
  buildingPermits: string;
}

export interface CityNeighborhood {
  name: string;
  type: string;
  rentPerSqftMin: number;
  rentPerSqftMax: number;
  trafficLevel: string;
  fitScore: number;
  beginnerFriendly: boolean;
  reason: string;
}

export interface CityProfile {
  city: string;
  state: string;
  stateAbbr: string;
  country?: string;
  currency?: string;
  currencySymbol?: string;
  wageMultiplier: number;
  rentMultiplier: number;
  links: CityLinks;
  neighborhoods: CityNeighborhood[];
  avgBuildoutPerSqft: number;
  salesTaxRate: number;
  beerWineLicenseCost: string;
  fullLiquorLicenseCost: string;
  beerWineLicenseTime: string;
  fullLiquorLicenseTime: string;
}

const CITY_PROFILES: Record<string, CityProfile> = {
  "miami": {
    city: "Miami", state: "Florida", stateAbbr: "FL",
    wageMultiplier: 1.05, rentMultiplier: 1.3,
    avgBuildoutPerSqft: 125, salesTaxRate: 7.0,
    beerWineLicenseCost: "$300–$1,000", fullLiquorLicenseCost: "$5,000–$14,000",
    beerWineLicenseTime: "30–60 days", fullLiquorLicenseTime: "3–6 months",
    links: {
      businessRegistration: "https://dos.fl.gov/sunbiz/start-business/",
      healthDepartment: "https://miamidade.gov/global/economy/food-establishments.page",
      zoning: "https://www.miamigov.com/Government/Departments-Organizations/Planning-Department/Zoning",
      alcoholBeerWine: "https://www.myfloridalicense.com/intentions2.asp?chession=&catid=2&divid=2",
      alcoholFullLiquor: "https://www.myfloridalicense.com/intentions2.asp?chession=&catid=2&divid=2",
      salesTax: "https://floridarevenue.com/taxes/taxesfees/pages/sales_tax.aspx",
      fireDepartment: "https://www.miamigov.com/Government/Departments-Organizations/Fire-Rescue",
      buildingPermits: "https://www.miamigov.com/Government/Departments-Organizations/Building-Department",
    },
    neighborhoods: [
      { name: "Brickell", type: "Premium Financial District", rentPerSqftMin: 45, rentPerSqftMax: 85, trafficLevel: "Very High", fitScore: 82, beginnerFriendly: false, reason: "Miami's financial center. Premium rents but massive foot traffic from office workers and residents." },
      { name: "Wynwood", type: "Arts & Nightlife", rentPerSqftMin: 35, rentPerSqftMax: 65, trafficLevel: "High", fitScore: 90, beginnerFriendly: true, reason: "Trendy arts district with strong tourist and local traffic. Great for creative food concepts." },
      { name: "Little Havana", type: "Cultural Hub", rentPerSqftMin: 18, rentPerSqftMax: 35, trafficLevel: "Medium-High", fitScore: 85, beginnerFriendly: true, reason: "Affordable rents with loyal local customer base and growing tourism." },
      { name: "Kendall", type: "Suburban", rentPerSqftMin: 15, rentPerSqftMax: 28, trafficLevel: "Medium", fitScore: 74, beginnerFriendly: true, reason: "Lower rents, family-oriented area. Good for casual dining and cafés." },
    ],
  },
  "new york": {
    city: "New York", state: "New York", stateAbbr: "NY",
    wageMultiplier: 1.45, rentMultiplier: 2.2,
    avgBuildoutPerSqft: 200, salesTaxRate: 8.875,
    beerWineLicenseCost: "$2,500–$4,500", fullLiquorLicenseCost: "$10,000–$35,000",
    beerWineLicenseTime: "2–4 months", fullLiquorLicenseTime: "4–8 months",
    links: {
      businessRegistration: "https://www.dos.ny.gov/corps/busguide.html",
      healthDepartment: "https://www.nyc.gov/site/doh/business/food-operators/food-service-establishment-permits.page",
      zoning: "https://www.nyc.gov/site/planning/zoning/zoning.page",
      alcoholBeerWine: "https://sla.ny.gov/how-to-apply",
      alcoholFullLiquor: "https://sla.ny.gov/how-to-apply",
      salesTax: "https://www.tax.ny.gov/bus/st/stidx.htm",
      fireDepartment: "https://www.nyc.gov/site/fdny/business/inspections/inspections.page",
      buildingPermits: "https://www.nyc.gov/site/buildings/homeowner/apply-for-a-permit.page",
    },
    neighborhoods: [
      { name: "East Village", type: "Trendy / Nightlife", rentPerSqftMin: 60, rentPerSqftMax: 120, trafficLevel: "Very High", fitScore: 86, beginnerFriendly: false, reason: "Dense foot traffic and young demographics. High competition but strong demand." },
      { name: "Williamsburg", type: "Hipster / Creative", rentPerSqftMin: 45, rentPerSqftMax: 90, trafficLevel: "High", fitScore: 88, beginnerFriendly: true, reason: "Brooklyn's hottest neighborhood. Food-forward and supports independent businesses." },
      { name: "Astoria", type: "Diverse / Growing", rentPerSqftMin: 30, rentPerSqftMax: 55, trafficLevel: "Medium-High", fitScore: 91, beginnerFriendly: true, reason: "Queens gem with diverse dining scene, lower rents, and strong community support." },
      { name: "Hell's Kitchen", type: "Theater / Tourist", rentPerSqftMin: 50, rentPerSqftMax: 95, trafficLevel: "Very High", fitScore: 85, beginnerFriendly: false, reason: "Restaurant Row destination. Massive pre- and post-theater traffic, but competitive." },
      { name: "Long Island City", type: "Emerging / Tech", rentPerSqftMin: 35, rentPerSqftMax: 60, trafficLevel: "High", fitScore: 89, beginnerFriendly: true, reason: "Rapid growth with new residential and office towers. Lower rents than Manhattan with strong demand." },
    ],
  },
  "houston": {
    city: "Houston", state: "Texas", stateAbbr: "TX",
    wageMultiplier: 0.90, rentMultiplier: 0.80,
    avgBuildoutPerSqft: 90, salesTaxRate: 8.25,
    beerWineLicenseCost: "$537", fullLiquorLicenseCost: "$1,200–$6,000",
    beerWineLicenseTime: "45–60 days", fullLiquorLicenseTime: "2–3 months",
    links: {
      businessRegistration: "https://www.sos.state.tx.us/corp/forms_option.shtml",
      healthDepartment: "https://www.houstontx.gov/health/Food-Inspection/",
      zoning: "https://www.houstontx.gov/planning/",
      alcoholBeerWine: "https://www.tabc.texas.gov/licensing/",
      alcoholFullLiquor: "https://www.tabc.texas.gov/licensing/",
      salesTax: "https://comptroller.texas.gov/taxes/sales/",
      fireDepartment: "https://www.houstontx.gov/fire/",
      buildingPermits: "https://www.houstontx.gov/planning/DevelopRegs/",
    },
    neighborhoods: [
      { name: "Montrose", type: "Trendy / Diverse", rentPerSqftMin: 22, rentPerSqftMax: 45, trafficLevel: "High", fitScore: 91, beginnerFriendly: true, reason: "Houston's most eclectic neighborhood. Strong dining scene with loyal, adventurous diners." },
      { name: "EaDo (East Downtown)", type: "Up-and-Coming", rentPerSqftMin: 18, rentPerSqftMax: 35, trafficLevel: "High", fitScore: 89, beginnerFriendly: true, reason: "Rapid development near sports stadiums. Great for bars, tacos, and fast-casual." },
      { name: "The Heights", type: "Historic / Family", rentPerSqftMin: 20, rentPerSqftMax: 40, trafficLevel: "Medium-High", fitScore: 87, beginnerFriendly: true, reason: "Family-friendly with walkable 19th St strip. Strong brunch and café demand." },
      { name: "Midtown", type: "Nightlife / Young Pro", rentPerSqftMin: 25, rentPerSqftMax: 50, trafficLevel: "Very High", fitScore: 85, beginnerFriendly: false, reason: "Dense nightlife area popular with young professionals. High traffic but competitive." },
    ],
  },
  "austin": {
    city: "Austin", state: "Texas", stateAbbr: "TX",
    wageMultiplier: 0.95, rentMultiplier: 1.0,
    avgBuildoutPerSqft: 100, salesTaxRate: 8.25,
    beerWineLicenseCost: "$600–$1,500", fullLiquorLicenseCost: "$3,000–$6,000",
    beerWineLicenseTime: "30–60 days", fullLiquorLicenseTime: "2–4 months",
    links: {
      businessRegistration: "https://www.sos.state.tx.us/corp/forms_702.shtml",
      healthDepartment: "https://www.austintexas.gov/department/food-establishment-requirements",
      zoning: "https://www.austintexas.gov/department/zoning",
      alcoholBeerWine: "https://www.tabc.texas.gov/licensing/",
      alcoholFullLiquor: "https://www.tabc.texas.gov/licensing/",
      salesTax: "https://comptroller.texas.gov/taxes/sales/",
      fireDepartment: "https://www.austintexas.gov/department/fire",
      buildingPermits: "https://www.austintexas.gov/department/building-permits",
    },
    neighborhoods: [
      { name: "South Congress (SoCo)", type: "Trendy / Tourist", rentPerSqftMin: 35, rentPerSqftMax: 65, trafficLevel: "Very High", fitScore: 87, beginnerFriendly: false, reason: "Iconic Austin strip. Premium location with tourists and locals, but expensive." },
      { name: "East Austin", type: "Up-and-Coming", rentPerSqftMin: 22, rentPerSqftMax: 45, trafficLevel: "High", fitScore: 93, beginnerFriendly: true, reason: "Austin's hottest growth area. Creative food scene and supportive community." },
      { name: "South Congress (SoCo)", type: "Trendy / Tourist", rentPerSqftMin: 35, rentPerSqftMax: 65, trafficLevel: "Very High", fitScore: 87, beginnerFriendly: false, reason: "Iconic Austin strip. Premium location with tourists and locals, but expensive." },
      { name: "The Domain", type: "Mixed-Use / Suburban", rentPerSqftMin: 30, rentPerSqftMax: 55, trafficLevel: "High", fitScore: 84, beginnerFriendly: true, reason: "North Austin's premier mixed-use development. Built-in foot traffic from retail and offices." },
      { name: "North Loop", type: "Local Favorite", rentPerSqftMin: 18, rentPerSqftMax: 35, trafficLevel: "Medium-High", fitScore: 85, beginnerFriendly: true, reason: "Charming neighborhood feel with loyal regulars and moderate rents." },
    ],
  },
  "los angeles": {
    city: "Los Angeles", state: "California", stateAbbr: "CA",
    wageMultiplier: 1.35, rentMultiplier: 1.8,
    avgBuildoutPerSqft: 175, salesTaxRate: 9.5,
    beerWineLicenseCost: "$1,500–$3,500", fullLiquorLicenseCost: "$12,000–$50,000+",
    beerWineLicenseTime: "2–3 months", fullLiquorLicenseTime: "3–12 months",
    links: {
      businessRegistration: "https://www.sos.ca.gov/business-programs/business-entities/",
      healthDepartment: "https://publichealth.lacounty.gov/eh/misc/plan-check.htm",
      zoning: "https://planning.lacity.gov/zoning/",
      alcoholBeerWine: "https://www.abc.ca.gov/licensing/",
      alcoholFullLiquor: "https://www.abc.ca.gov/licensing/",
      salesTax: "https://www.cdtfa.ca.gov/taxes-and-fees/sales-use-tax-rates.htm",
      fireDepartment: "https://www.lafd.org/fire-prevention/inspections",
      buildingPermits: "https://www.ladbs.org/services/getting-started/apply-for-permit",
    },
    neighborhoods: [
      { name: "Silver Lake", type: "Trendy / Creative", rentPerSqftMin: 35, rentPerSqftMax: 70, trafficLevel: "High", fitScore: 88, beginnerFriendly: true, reason: "Creative neighborhood with strong brunch and café culture." },
      { name: "Highland Park", type: "Up-and-Coming", rentPerSqftMin: 25, rentPerSqftMax: 50, trafficLevel: "Medium-High", fitScore: 91, beginnerFriendly: true, reason: "NE LA's rising star. Affordable with growing food scene." },
      { name: "Culver City", type: "Tech Hub", rentPerSqftMin: 30, rentPerSqftMax: 60, trafficLevel: "High", fitScore: 86, beginnerFriendly: true, reason: "Booming with tech workers. Strong lunch and dinner demand." },
      { name: "Echo Park", type: "Artsy / Local", rentPerSqftMin: 22, rentPerSqftMax: 45, trafficLevel: "Medium-High", fitScore: 89, beginnerFriendly: true, reason: "Artsy neighborhood with loyal locals. Lower rents and strong indie restaurant scene." },
    ],
  },
  "chicago": {
    city: "Chicago", state: "Illinois", stateAbbr: "IL",
    wageMultiplier: 1.15, rentMultiplier: 1.1,
    avgBuildoutPerSqft: 115, salesTaxRate: 10.25,
    beerWineLicenseCost: "$700–$2,000", fullLiquorLicenseCost: "$4,400–$8,800",
    beerWineLicenseTime: "30–90 days", fullLiquorLicenseTime: "2–4 months",
    links: {
      businessRegistration: "https://www.ilsos.gov/departments/business_services/",
      healthDepartment: "https://www.chicago.gov/city/en/depts/cdph/provdrs/food_safety.html",
      zoning: "https://www.chicago.gov/city/en/depts/dcd/provdrs/admin/svcs/zoning_certificates.html",
      alcoholBeerWine: "https://www.chicago.gov/city/en/depts/bacp/supp_info/liquor_license.html",
      alcoholFullLiquor: "https://www.chicago.gov/city/en/depts/bacp/supp_info/liquor_license.html",
      salesTax: "https://tax.illinois.gov/businesses/taxregistration.html",
      fireDepartment: "https://www.chicago.gov/city/en/depts/cfd.html",
      buildingPermits: "https://www.chicago.gov/city/en/depts/bldgs/provdrs/permits.html",
    },
    neighborhoods: [
      { name: "Wicker Park", type: "Trendy / Nightlife", rentPerSqftMin: 28, rentPerSqftMax: 55, trafficLevel: "Very High", fitScore: 89, beginnerFriendly: true, reason: "Vibrant nightlife and dining scene. Great for bars and creative restaurants." },
      { name: "Logan Square", type: "Up-and-Coming", rentPerSqftMin: 22, rentPerSqftMax: 42, trafficLevel: "High", fitScore: 92, beginnerFriendly: true, reason: "One of Chicago's hottest areas. Strong community support for independent businesses." },
      { name: "River North", type: "Premium / Tourist", rentPerSqftMin: 40, rentPerSqftMax: 80, trafficLevel: "Very High", fitScore: 83, beginnerFriendly: false, reason: "Chicago's premier dining and nightlife district. High rents but massive foot traffic." },
      { name: "Pilsen", type: "Cultural / Artsy", rentPerSqftMin: 16, rentPerSqftMax: 32, trafficLevel: "Medium-High", fitScore: 86, beginnerFriendly: true, reason: "Rich culture, affordable rents, and a loyal local customer base." },
    ],
  },
  "nashville": {
    city: "Nashville", state: "Tennessee", stateAbbr: "TN",
    wageMultiplier: 0.90, rentMultiplier: 0.85,
    avgBuildoutPerSqft: 95, salesTaxRate: 9.25,
    beerWineLicenseCost: "$500–$1,000", fullLiquorLicenseCost: "$1,500–$4,000",
    beerWineLicenseTime: "30–60 days", fullLiquorLicenseTime: "2–4 months",
    links: {
      businessRegistration: "https://sos.tn.gov/business-services",
      healthDepartment: "https://www.nashville.gov/departments/health/environmental-health/food-protection",
      zoning: "https://www.nashville.gov/departments/planning/zoning",
      alcoholBeerWine: "https://www.nashville.gov/departments/metropolitan-clerk/beer-board",
      alcoholFullLiquor: "https://www.tn.gov/abc.html",
      salesTax: "https://www.tn.gov/revenue/taxes/sales-and-use-tax.html",
      fireDepartment: "https://www.nashville.gov/departments/fire",
      buildingPermits: "https://www.nashville.gov/departments/codes",
    },
    neighborhoods: [
      { name: "The Gulch", type: "Premium / Trendy", rentPerSqftMin: 35, rentPerSqftMax: 65, trafficLevel: "Very High", fitScore: 87, beginnerFriendly: false, reason: "Nashville's trendiest area. High rents but massive foot traffic from tourists and locals." },
      { name: "12South", type: "Hip / Local", rentPerSqftMin: 28, rentPerSqftMax: 50, trafficLevel: "High", fitScore: 90, beginnerFriendly: true, reason: "Walkable, community-focused strip. Great for brunch spots, cafés, and boutique dining." },
      { name: "East Nashville", type: "Up-and-Coming", rentPerSqftMin: 20, rentPerSqftMax: 40, trafficLevel: "High", fitScore: 92, beginnerFriendly: true, reason: "Creative, diverse food scene. Affordable rents and strong community support." },
      { name: "Germantown", type: "Historic / Growing", rentPerSqftMin: 25, rentPerSqftMax: 45, trafficLevel: "Medium-High", fitScore: 88, beginnerFriendly: true, reason: "Historic charm with growing restaurant scene. Near downtown but more affordable." },
    ],
  },
  "london": {
    city: "London", state: "England", stateAbbr: "UK",
    country: "UK", currency: "GBP", currencySymbol: "£",
    wageMultiplier: 1.10, rentMultiplier: 2.0,
    avgBuildoutPerSqft: 150, salesTaxRate: 20.0,
    beerWineLicenseCost: "£100–£500", fullLiquorLicenseCost: "£100–£1,900",
    beerWineLicenseTime: "4–8 weeks", fullLiquorLicenseTime: "4–8 weeks",
    links: {
      businessRegistration: "https://www.gov.uk/set-up-business",
      healthDepartment: "https://www.food.gov.uk/business-guidance",
      zoning: "https://www.london.gov.uk/programmes-strategies/planning",
      alcoholBeerWine: "https://www.gov.uk/premises-licence",
      alcoholFullLiquor: "https://www.gov.uk/premises-licence",
      salesTax: "https://www.gov.uk/vat-registration",
      fireDepartment: "https://www.london-fire.gov.uk/",
      buildingPermits: "https://www.gov.uk/planning-permission-england-wales",
    },
    neighborhoods: [
      { name: "Shoreditch", type: "Trendy / Creative", rentPerSqftMin: 45, rentPerSqftMax: 90, trafficLevel: "Very High", fitScore: 88, beginnerFriendly: false, reason: "East London's trendiest food scene. High rents but massive foot traffic and Instagram appeal." },
      { name: "Peckham", type: "Up-and-Coming", rentPerSqftMin: 25, rentPerSqftMax: 50, trafficLevel: "High", fitScore: 92, beginnerFriendly: true, reason: "South London's rising star. Affordable rents, diverse community, and growing restaurant scene." },
      { name: "Brixton", type: "Cultural / Diverse", rentPerSqftMin: 28, rentPerSqftMax: 55, trafficLevel: "High", fitScore: 89, beginnerFriendly: true, reason: "Vibrant food market area. Strong local loyalty and growing tourist interest." },
      { name: "Soho", type: "Premium / Central", rentPerSqftMin: 60, rentPerSqftMax: 120, trafficLevel: "Very High", fitScore: 82, beginnerFriendly: false, reason: "West End's dining heart. Extremely expensive but unbeatable foot traffic." },
      { name: "Borough Market Area", type: "Food Destination", rentPerSqftMin: 40, rentPerSqftMax: 80, trafficLevel: "Very High", fitScore: 86, beginnerFriendly: false, reason: "London's premier food destination. Built-in foodie audience but competitive." },
    ],
  },
  "manchester": {
    city: "Manchester", state: "England", stateAbbr: "UK",
    country: "UK", currency: "GBP", currencySymbol: "£",
    wageMultiplier: 0.85, rentMultiplier: 0.70,
    avgBuildoutPerSqft: 90, salesTaxRate: 20.0,
    beerWineLicenseCost: "£100–£300", fullLiquorLicenseCost: "£100–£900",
    beerWineLicenseTime: "4–8 weeks", fullLiquorLicenseTime: "4–8 weeks",
    links: {
      businessRegistration: "https://www.gov.uk/set-up-business",
      healthDepartment: "https://www.manchester.gov.uk/info/200074/health",
      zoning: "https://www.manchester.gov.uk/info/200074/planning",
      alcoholBeerWine: "https://www.manchester.gov.uk/info/200063/licences_and_permissions",
      alcoholFullLiquor: "https://www.manchester.gov.uk/info/200063/licences_and_permissions",
      salesTax: "https://www.gov.uk/vat-registration",
      fireDepartment: "https://www.manchesterfire.gov.uk/",
      buildingPermits: "https://www.manchester.gov.uk/info/200074/planning",
    },
    neighborhoods: [
      { name: "Ancoats", type: "Trendy / Up-and-Coming", rentPerSqftMin: 18, rentPerSqftMax: 35, trafficLevel: "High", fitScore: 93, beginnerFriendly: true, reason: "Manchester's hottest food neighborhood. Converted mills with affordable rents and creative energy." },
      { name: "Northern Quarter", type: "Creative / Nightlife", rentPerSqftMin: 22, rentPerSqftMax: 45, trafficLevel: "Very High", fitScore: 90, beginnerFriendly: true, reason: "Independent spirit with strong café and bar culture. Great for creative concepts." },
      { name: "Didsbury", type: "Suburban / Affluent", rentPerSqftMin: 15, rentPerSqftMax: 30, trafficLevel: "Medium-High", fitScore: 87, beginnerFriendly: true, reason: "Affluent suburb with families. Lower rents and loyal local customer base." },
      { name: "Deansgate", type: "Premium / Central", rentPerSqftMin: 30, rentPerSqftMax: 55, trafficLevel: "Very High", fitScore: 84, beginnerFriendly: false, reason: "City centre's main strip. High visibility and foot traffic but premium rents." },
    ],
  },
  "edinburgh": {
    city: "Edinburgh", state: "Scotland", stateAbbr: "UK",
    country: "UK", currency: "GBP", currencySymbol: "£",
    wageMultiplier: 0.88, rentMultiplier: 0.80,
    avgBuildoutPerSqft: 95, salesTaxRate: 20.0,
    beerWineLicenseCost: "£200–£600", fullLiquorLicenseCost: "£200–£1,200",
    beerWineLicenseTime: "6–10 weeks", fullLiquorLicenseTime: "6–10 weeks",
    links: {
      businessRegistration: "https://www.gov.uk/set-up-business",
      healthDepartment: "https://www.edinburgh.gov.uk/environmental-health",
      zoning: "https://www.edinburgh.gov.uk/planning-building",
      alcoholBeerWine: "https://www.edinburgh.gov.uk/licences-permits",
      alcoholFullLiquor: "https://www.edinburgh.gov.uk/licences-permits",
      salesTax: "https://www.gov.uk/vat-registration",
      fireDepartment: "https://www.firescotland.gov.uk/",
      buildingPermits: "https://www.edinburgh.gov.uk/planning-building",
    },
    neighborhoods: [
      { name: "Leith", type: "Up-and-Coming / Port", rentPerSqftMin: 15, rentPerSqftMax: 32, trafficLevel: "High", fitScore: 93, beginnerFriendly: true, reason: "Edinburgh's hottest food scene. Former port area with creative restaurants and affordable rents." },
      { name: "Stockbridge", type: "Village / Affluent", rentPerSqftMin: 20, rentPerSqftMax: 40, trafficLevel: "Medium-High", fitScore: 89, beginnerFriendly: true, reason: "Charming village feel within the city. Strong brunch and café culture." },
      { name: "Grassmarket", type: "Tourist / Historic", rentPerSqftMin: 25, rentPerSqftMax: 50, trafficLevel: "Very High", fitScore: 85, beginnerFriendly: false, reason: "Historic Old Town location. Heavy tourist traffic during Festival season." },
      { name: "Bruntsfield", type: "Residential / Local", rentPerSqftMin: 18, rentPerSqftMax: 35, trafficLevel: "Medium-High", fitScore: 90, beginnerFriendly: true, reason: "Walkable residential area with loyal regulars. Great for neighbourhood restaurants." },
    ],
  },
};

// Default fallback for unknown cities
const DEFAULT_PROFILE: CityProfile = {
  city: "Your City", state: "Your State", stateAbbr: "",
  wageMultiplier: 1.0, rentMultiplier: 1.0,
  avgBuildoutPerSqft: 110, salesTaxRate: 7.0,
  beerWineLicenseCost: "$500–$2,000", fullLiquorLicenseCost: "$3,000–$14,000",
  beerWineLicenseTime: "1–3 months", fullLiquorLicenseTime: "3–6 months",
  links: {
    businessRegistration: "https://www.sba.gov/business-guide/launch-your-business/register-your-business",
    healthDepartment: "https://www.fda.gov/food/retail-food-industrie/retail-food-protection",
    zoning: "https://www.sba.gov/business-guide/launch-your-business/pick-your-business-location",
    alcoholBeerWine: "https://www.ttb.gov/alcohol/beverage-alcohol",
    alcoholFullLiquor: "https://www.ttb.gov/alcohol/beverage-alcohol",
    salesTax: "https://www.sba.gov/business-guide/manage-your-business/pay-taxes",
    fireDepartment: "https://www.usfa.fema.gov/prevention/outreach/",
    buildingPermits: "https://www.sba.gov/business-guide/launch-your-business/apply-for-licenses-and-permits",
  },
  neighborhoods: [
    { name: "Downtown Core", type: "Premium / High Traffic", rentPerSqftMin: 30, rentPerSqftMax: 60, trafficLevel: "Very High", fitScore: 84, beginnerFriendly: false, reason: "Maximum visibility but competitive and expensive." },
    { name: "Arts / Emerging District", type: "Up-and-Coming", rentPerSqftMin: 18, rentPerSqftMax: 35, trafficLevel: "Medium-High", fitScore: 90, beginnerFriendly: true, reason: "Growing area with creative energy. Best value for your budget." },
    { name: "University Area", type: "Budget-Friendly", rentPerSqftMin: 12, rentPerSqftMax: 25, trafficLevel: "High", fitScore: 80, beginnerFriendly: true, reason: "Affordable rents with steady customer traffic." },
    { name: "Suburban Strip", type: "Budget-Friendly", rentPerSqftMin: 10, rentPerSqftMax: 20, trafficLevel: "Medium", fitScore: 72, beginnerFriendly: true, reason: "Lowest rent, easy parking. Strong local customer base." },
  ],
};

export function getCityProfile(city: string): CityProfile {
  const key = city.toLowerCase().trim();
  
  // Direct match
  if (CITY_PROFILES[key]) return { ...CITY_PROFILES[key] };
  
  // Partial match
  for (const [k, v] of Object.entries(CITY_PROFILES)) {
    if (key.includes(k) || k.includes(key)) return { ...v };
  }
  
  return { ...DEFAULT_PROFILE, city: city || "Your City" };
}

export function getCityCurrency(city: string): { symbol: string; code: string; isUK: boolean } {
  const info = getCurrencyForCity(city);
  const profile = getCityProfile(city);
  return { symbol: info.symbol, code: info.code, isUK: profile.country === "UK" };
}
